import React, { useState, useEffect, useCallback } from 'react'
import { useCMSContext } from './../../CMSContext'
// Material-UI
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import TablePagination from '@mui/material/TablePagination'
import './NewsAndAnnouncements.scss'
import { MdEdit, MdVisibility } from 'react-icons/md'
import CircularProgress from '@mui/material/CircularProgress'
import { IARDialog } from '../../../Shared/Dialog/IARDialog'
import NewsDialog from '../../../Shared/NewsDialog/NewsDialog'
import NewsAndAnnouncementsActions from './NewsAndAnnouncementsActions'
import { useErrorSnackBarContext } from '../../../Shared/ErrorSnackBar/ErrorSnackBarContext'
import Interceptor from '../../../../Services/serviceBase.interceptor'
import TableContainer from '@mui/material/TableContainer'
import TableFooter from '@mui/material/TableFooter'
import Checkbox from '@mui/material/Checkbox'
import { parseDate } from '../../../../../resources/Utils'
import { CustomSwitch } from '../../../Shared/Switch/switch'
import NewsPreviewActions from './NewsPreviewActions'

function NewsAndAnnouncements() {
  Interceptor.interceptor(useErrorSnackBarContext().errorSnackBarActions)
  const cmsContext = useCMSContext()
  // State for confirmation dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false })
  // State for edit dialog
  const [postDialog, setPostDialog] = useState({ open: false })
  const [isItNew, setIsItNew] = useState(false)
  const { fireDepartment, socket, iarMember, isReadOnlyMember, appConfiguration } = cmsContext.state
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [page, setPage] = useState(0)
  const [rowState, setRowState] = useState({})
  const [newsEntity, setNewsEntity] = useState({ newsAnnouncements: [] })
  const [paginationIds, setPaginationIds] = useState({ first: '', last: '' })
  const [pageId, setPageId] = useState([])
  const [allSelected, setAllSelected] = useState(false)
  const [inEditionNews, setInEditionNews] = useState([])
  const [posting, setPosting] = useState(false)
  const [disabledButton, setDisabledButton] = useState(false)
  const [socketResponse, setSocketResponse] = useState(null)
  const [showPublishedContent, setShowPublishedContent] = useState(true)
  const [controlDialog, setControlDialog] = useState(false)

  const fetchNews = useCallback(
    async (newPage, dateFrom = undefined) => {
      if (!fireDepartment) return

      let response

      if (!showPublishedContent && appConfiguration?.previewEnabled) {
        response = await NewsPreviewActions.getAll(fireDepartment.identifier, dateFrom)
      } else {
        response = await NewsAndAnnouncementsActions.getNews(
          fireDepartment.identifier,
          dateFrom ?? 0
        )
      }

      if (!response) return

      if (response.newsAnnouncements.length) {
        setPaginationIds({
          first: paginationIds.last,
          last: response.newsAnnouncements[response.newsAnnouncements.length - 1].date
        })
        setPage(newPage)
      }
      setNewsEntity(response)
    },
    [
      appConfiguration?.previewEnabled,
      fireDepartment.identifier,
      paginationIds.last,
      showPublishedContent
    ]
  )

  const pageChange = useCallback(
    (newPage) => {
      const pageIds = pageId
      setAllSelected(false)
      if (newPage > page) {
        pageIds.push({ page: newPage, id: paginationIds.last })
        setPageId(pageIds)
      }
      const id =
        newPage === 0 ? undefined : pageId.filter((i) => i.page === newPage).slice(-1)[0].id
      fetchNews(newPage, id)
    },
    [fetchNews, page, pageId, paginationIds]
  )

  useEffect(() => {
    if (!fireDepartment || !socket) return

    socket.on(`${fireDepartment.fireDepartmentId.toString()}_inner_news_list`, (args) => {
      console.log(`CMS_inner_news list`, args, new Date())
      setInEditionNews(args.ids)

      if (args.param === 'saved') {
        if (iarMember && args.memberId === iarMember.memberId) {
          fetchNews(0)
        }
        if (args.response) {
          setSocketResponse({
            ...args.response,
            channel: `${fireDepartment.fireDepartmentId.toString()}_inner_news_list`
          })
        }
      }

      if (args.param === 'deleted') {
        fetchNews(0)
      }
    })
    return () => {
      socket.off(`${fireDepartment.fireDepartmentId.toString()}_inner_news_list`)
    }
  }, [fireDepartment, socket, newsEntity, iarMember, fetchNews])

  useEffect(() => {
    if (rowState._id === '') {
      setIsItNew(true)
      setPostDialog({ open: true })
    }
  }, [rowState])

  useEffect(() => {
    pageChange(page)
  }, [showPublishedContent])

  const handleChangePage = (event, newPage) => {
    pageChange(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const openConfirmationDialog = () => {
    setDeleteDialog({ open: true })
  }

  const deleteRecord = useCallback(() => {
    async function fetchNewsDelete() {
      var idsNews = newsEntity.newsAnnouncements
        .map((el) => {
          if (el.isSelected) {
            return el._id
          } else {
            return null
          }
        })
        .filter((el) => el !== undefined)
      const response = await NewsAndAnnouncementsActions.deleteNews(idsNews)
      setDeleteDialog({ open: false })
      if (response && newsEntity.newsAnnouncements?.length - 1 > 0) {
        pageChange(page)
      }
    }
    fetchNewsDelete()
  }, [newsEntity, page, pageChange])

  const cancelDialog = () => {
    setDeleteDialog({ open: false })
  }

  const openPostNewsDialog = () => {
    setRowState({ _id: '' })
    setControlDialog(false)
  }

  const closePostNewsDialog = (wasARecordAdded) => {
    setRowState({})
    setPostDialog({ open: false })
    if (wasARecordAdded) {
      handleChangePage(null, page)
    }
  }

  const openEditPostDialog = (item, edit) => {
    setRowState(item)
    setPostDialog({ open: true })
    setControlDialog(edit)
  }

  const handleRowClick = useCallback(
    (id) => {
      if (inEditionNews?.includes(id)) return

      var newsSelected = newsEntity.newsAnnouncements.find((el) => el._id === id)
      var arrayNews = newsEntity.newsAnnouncements
      var indexOfSelected = arrayNews.findIndex((el) => el._id === id)
      newsSelected.isSelected = !newsSelected.isSelected
      arrayNews[indexOfSelected] = newsSelected

      setNewsEntity({
        ...newsEntity,
        newsAnnouncements: arrayNews
      })
    },
    [newsEntity]
  )

  const onSelectAllClick = useCallback(() => {
    var newsAnnouncements = newsEntity.newsAnnouncements?.map((x) => ({
      ...x,
      isSelected: !inEditionNews?.includes(x._id) && !allSelected
    }))
    setNewsEntity({
      ...newsEntity,
      newsAnnouncements
    })
    setAllSelected(!allSelected)
  }, [newsEntity, allSelected, inEditionNews])

  const switchContent = () => {
    setPaginationIds({ first: '0', last: '0' })
    setPage(0)
    setPageId([{ page: 0, id: 0 }])
    setShowPublishedContent((prev) => !prev)
  }

  return (
    <div className="App news-and-announcements">
      {postDialog.open && (
        <NewsDialog
          id={rowState._id}
          title={
            <NewsDialogTitle
              isPreviewEnabled={appConfiguration?.previewEnabled}
              isPreview={!showPublishedContent}
              isNew={!rowState?._id}
            />
          }
          open={postDialog.open}
          fireDepartmentIdentifier={fireDepartment.identifier}
          onCancel={closePostNewsDialog}
          onClose={closePostNewsDialog}
          isItNew={isItNew}
          posting={posting}
          setPosting={setPosting}
          disabledButton={disabledButton}
          setDisabledButton={setDisabledButton}
          socketResponse={socketResponse}
          showPublished={!appConfiguration?.previewEnabled || showPublishedContent || controlDialog}
        />
      )}
      <IARDialog
        open={deleteDialog.open}
        title="Confirm"
        content={
          <div className="dialogContent">
            <div>Do you want to delete the selected records?</div>
            <div>This action will delete published and preview content.</div>
            <ul>
              {newsEntity.newsAnnouncements
                .filter((x) => x.isSelected)
                .map((el) => (
                  <li key={el._id}>{el.title}</li>
                ))}
            </ul>
          </div>
        }
        onOk={deleteRecord}
        onCancel={cancelDialog}
      />
      <div className="page-header">News</div>
      <Container maxWidth="lg">
        <Grid container spacing={2}>
          <Grid size={12}>
            <div className="page-description">
              Your community will want to hear from you about updates regarding your organization,
              your services, your activities, and your accomplishments! Instead of sending an alert,
              publish a news article here each time you have something to share to keep your
              community informed and engaged. This is your opportunity to control the content and
              frequency of what the community hears about the great work that you do. The more they
              know about the valuable work and services you provide, the more supportive they will
              be of your needs!
            </div>
          </Grid>
          <Grid size={12}>
            <div className="controls-container">
              {appConfiguration?.previewEnabled && (
                <Grid size={6} className="container-field">
                  <span>Display Content as PREVIEW MODE</span>
                  <CustomSwitch
                    name="showPublished"
                    value={!showPublishedContent}
                    sectionActiveChange={switchContent}
                    useYesNoButtons={true}
                  />
                </Grid>
              )}
              <div className="write-button">
                {newsEntity.newsAnnouncements.some((el) => el.isSelected) && (
                  <Button
                    variant="contained"
                    id="deleteNewsButton"
                    size="medium"
                    className="deleteNewsButton"
                    onClick={openConfirmationDialog}
                    disabled={isReadOnlyMember}
                  >
                    Delete News
                  </Button>
                )}

                <Button
                  style={{ maxWidth: '250px', minWidth: '250px', right: 0 }}
                  variant="contained"
                  id="writeNewsButton"
                  color="primary"
                  size="medium"
                  onClick={openPostNewsDialog}
                  disabled={
                    isReadOnlyMember || (appConfiguration.previewEnabled && showPublishedContent)
                  }
                >
                  Write News
                </Button>
              </div>
            </div>
            <div className="cannot-edit-wrapper">
              {appConfiguration.previewEnabled && !showPublishedContent && (
                <div
                  className="cannot-edit-label"
                  style={{ marginLeft: '30px', marginTop: '15px' }}
                >
                  The list you see below contains NEWS in its PREVIEW MODE and an option to review
                  their PUBLISHED CONTENT. You can add, edit, or delete only NEWS in its PREVIEW
                  MODE.
                </div>
              )}
              {appConfiguration.previewEnabled && showPublishedContent && (
                <div
                  className="cannot-edit-label"
                  style={{ marginLeft: '30px', marginTop: '15px' }}
                >
                  The list you see below contains only PUBLISHED NEWS and every community member
                  using MyLS app can visualize it as is. To edit one of them, you need to set
                  "Display Content as PREVIEW MODE" to Yes and start making changes.
                </div>
              )}
            </div>
          </Grid>
          <Grid size={12} className="table-container">
            <Paper sx={{ width: '100%' }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow className="table-header">
                      <TableCell>
                        <Checkbox
                          onChange={onSelectAllClick}
                          inputProps={{ 'aria-label': '' }}
                          checked={allSelected}
                        />
                        News
                      </TableCell>
                      <TableCell align="right">
                        {appConfiguration?.previewEnabled ? 'Created at' : 'Date Published'}
                      </TableCell>
                      <TableCell align="right"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {newsEntity.newsAnnouncements?.map((row) => (
                      <TableRow key={row._id} hover selected={row.isSelected}>
                        <TableCell
                          component="th"
                          scope="row"
                          className="newsTitle"
                          onClick={() => handleRowClick(row._id)}
                        >
                          {row.title}
                        </TableCell>
                        <TableCell
                          align="right"
                          className="newsDatePublished"
                          onClick={() => handleRowClick(row._id)}
                        >
                          {parseDate(row.localDate, 'HH:mm MMM dd, y')}
                        </TableCell>
                        <TableCell align="right">
                          <CmsGridIcon
                            isEditing={inEditionNews?.includes(row._id)}
                            previewEnabled={appConfiguration.previewEnabled}
                            isPreview={!showPublishedContent}
                            hasPublished={row.published}
                            onClick={() => openEditPostDialog(row, true)}
                            onClickEdit={() => openEditPostDialog(row, false)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={[10]}
                        count={newsEntity?.total || 0}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </div>
  )
}

function CmsGridIcon({ isEditing, isPreview, previewEnabled, hasPublished, onClick, onClickEdit }) {
  const handleOnClick = useCallback((e) => onClick?.(e), [onClick])
  const handleOnClickEdit = useCallback((e) => onClickEdit?.(e), [onClickEdit])

  if (previewEnabled) {
    if (isPreview) {
      if (isEditing) {
        return <CircularProgress className="grid-icon" size={20} />
      } else if (isPreview) {
        if (hasPublished) {
          return (
            <>
              {' '}
              <MdEdit className="grid-icon" onClick={handleOnClickEdit} />{' '}
              <MdVisibility className="grid-icon" onClick={handleOnClick} />{' '}
            </>
          )
        }

        return (
          <>
            {' '}
            <MdEdit className="grid-icon" onClick={handleOnClickEdit} />{' '}
            <MdVisibility className="grid-icon-disabled" disabled />{' '}
          </>
        )
      }
    } else {
      return <MdVisibility className="grid-icon" onClick={handleOnClick} />
    }
  } else {
    if (isEditing) return <CircularProgress className="grid-icon" size={20} />
    else return <MdEdit className="grid-icon" onClick={handleOnClickEdit} />
  }
}

function NewsDialogTitle({ isPreviewEnabled, isPreview, isNew }) {
  if (!isPreviewEnabled) return 'Write News'
  else if (isNew) return 'Create news content'
  else if (isPreview) return 'Edit news content'
  else return 'News content details'
}

export default NewsAndAnnouncements
